import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import SEO from '@/components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    try {
      setIsSubmitting(true);
      await axios.post(`${API}/contact`, formData);
      toast.success('Mensagem enviada com sucesso! Entraremos em contato em breve.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: '',
      });
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      toast.error('Erro ao enviar mensagem. Por favor, tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email',
      content: 'contato@grantour360.com.br',
      link: 'mailto:contato@grantour360.com.br',
    },
    {
      icon: Phone,
      title: 'WhatsApp',
      content: '(41) 99945-5042',
      link: 'https://wa.me/5541999455042',
    },
    {
      icon: MapPin,
      title: 'Localização',
      content: 'Curitiba - PR, Brasil',
      link: null,
    },
  ];

  const contactSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contato Gran Tour 360",
    "description": "Entre em contato conosco para solicitar um orçamento de Tour Virtual 360° em Curitiba.",
    "url": "https://grantour360.com.br/contato",
    "mainEntity": {
      "@type": "LocalBusiness",
      "name": "Gran Tour 360",
      "image": "https://grantour360.com.br/images/logo.png",
      "telephone": "+5541999455042",
      "areaServed": {
        "@type": "City",
        "name": "Curitiba",
        "containedInPlace": {
          "@type": "State",
          "name": "Paraná"
        }
      }
    }
  };

  return (
    <div className="min-h-screen bg-white pt-32 pb-24" data-testid="contact-page">
      <SEO 
        title="Contato e Orçamento | Gran Tour 360"
        description="Solicite um orçamento para tours virtuais 360° profissionais. Fale com a equipe Gran Tour 360 e dê o primeiro passo para transformar seus espaços."
        url="https://grantour360.com.br/contato"
        schema={contactSchema}
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-[#FF3366] text-xs uppercase tracking-[0.2em] font-semibold mb-4">Contato</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 tracking-tight">
              Seu Projeto Começa Aqui
            </h1>
            <p className="text-gray-500 max-w-3xl mx-auto text-base sm:text-lg leading-relaxed">
              Conte-nos sobre o que você precisa. Nossa equipe responde rápido e com propostas sob medida para o seu negócio.
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {contactInfo.map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white border border-gray-200 p-8 rounded-lg text-center hover:shadow-lg hover:border-[#FF3366]/30 transition-all"
                data-testid={`contact-info-${index}`}
              >
                <div className="w-16 h-16 bg-[#FF3366]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Icon size={28} className="text-[#FF3366]" strokeWidth={1.5} />
                </div>
                <h3 className="text-gray-900 font-semibold mb-2">{item.title}</h3>
                {item.link ? (
                  <a
                    href={item.link}
                    target={item.link.startsWith('http') ? '_blank' : undefined}
                    rel={item.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className="text-gray-500 hover:text-[#FF3366] transition-colors"
                  >
                    {item.content}
                  </a>
                ) : (
                  <p className="text-gray-500">{item.content}</p>
                )}
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-3xl mx-auto"
        >
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 lg:p-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">Envie sua Mensagem</h2>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-gray-700">
                  Nome *
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="bg-white border-gray-300 text-gray-900 focus:border-[#FF3366] focus:ring-[#FF3366]"
                  placeholder="Seu nome completo"
                  data-testid="contact-name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-700">
                  Email *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="bg-white border-gray-300 text-gray-900 focus:border-[#FF3366] focus:ring-[#FF3366]"
                  placeholder="seu@email.com"
                  data-testid="contact-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-gray-700">
                  Telefone
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  className="bg-white border-gray-300 text-gray-900 focus:border-[#FF3366] focus:ring-[#FF3366]"
                  placeholder="(00) 00000-0000"
                  data-testid="contact-phone"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="text-gray-700">
                  Mensagem *
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="bg-white border-gray-300 text-gray-900 focus:border-[#FF3366] focus:ring-[#FF3366] resize-none"
                  placeholder="Conte-nos sobre seu projeto..."
                  data-testid="contact-message"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#FF3366] hover:bg-[#FF5C85] text-white font-semibold py-6 text-base"
                data-testid="contact-submit"
              >
                {isSubmitting ? (
                  'Enviando...'
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Send size={20} />
                    Enviar Mensagem
                  </span>
                )}
              </Button>
            </form>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Contact;
